import React, { FC, useEffect, useRef, useState } from 'react';
import {
  DataGrid,
  DynamicPagination,
  FlexGrid,
  getClassNameFactory,
} from '@dcl/ui';
import { IPaginationBarProps } from '@dcl/ui/dist/fragments/DataGrid/DataGridTypes';
import {
  ERROR_MESSAGE,
  ITEMS_PER_PAGE,
  NEXT_LABEL,
  PAGINATION_CONFIG,
  PAGINATION_LABEL,
  PREVIOUS_LABEL,
  RESULTS_PER_PAGE_LABEL,
  ROWS_PER_PAGE_LABEL_CONTENT,
  PAGE_LABEL,
} from '../../constants';
import { IFileDownloadExperienceSearchResultDekstop } from './types';
import './FileDownloadExperienceSearchResultDekstop.css';
import { activeSVGString } from '../../icons/ActiveIcon';
import { inactiveSVGString } from '../../icons/InActiveIcon';

export const FileDownloadExperienceSearchResultDekstop: FC<IFileDownloadExperienceSearchResultDekstop> = (
  props
) => {
  const { columns, data, serachFlag } = props;
  const activeFilters = useRef(new Set());
  const [lastClickedFilterButton, setLastClickedFilterButton] = useState<
    number | null
  >(null);
  const [modalOpen, setModalOpen] = useState(false);

  const getClassName = getClassNameFactory(
    FileDownloadExperienceSearchResultDekstop.displayName
  );

  useEffect(() => {
    // Function to check if filter modal is open
    const checkIfModalOpen = () => {
      const modal =
        document.querySelector('.DataGrid-modal') ||
        document.querySelector('[role="dialog"]') ||
        document.querySelector('.modal');
      return !!modal && window.getComputedStyle(modal).display !== 'none';
    };

    const handleFilterButtonClick = (event) => {
      try {
        const filterButton = event.target.closest('.tr-DataGrid-filter');
        if (!filterButton) return;

        const buttonId = Array.from(
          document.querySelectorAll('.tr-DataGrid-filter')
        ).indexOf(filterButton);

        // If this is a new click on the filter button (not from within the modal)
        if (!modalOpen) {
          setLastClickedFilterButton(buttonId);
          setModalOpen(true);
        }
      } catch (error) {
        console.error('Error in filter button click handler:', error);
      }
    };

    const handleModalButtonClick = (event) => {
      try {
        // Check if it's an Apply Filter button
        if (event.target.closest('.tr-Button--primary')) {
          if (lastClickedFilterButton !== null) {
            const filterButtons = document.querySelectorAll(
              '.tr-DataGrid-filter'
            );
            if (filterButtons[lastClickedFilterButton]) {
              filterButtons[
                lastClickedFilterButton
              ].innerHTML = activeSVGString;
              activeFilters.current.add(lastClickedFilterButton);
            }
          }
          setModalOpen(false);
        }

        // Check if it's a Clear Filter button
        else if (event.target.closest('.tr-Button--tertiary')) {
          if (lastClickedFilterButton !== null) {
            const filterButtons = document.querySelectorAll(
              '.tr-DataGrid-filter'
            );
            if (filterButtons[lastClickedFilterButton]) {
              filterButtons[
                lastClickedFilterButton
              ].innerHTML = inactiveSVGString;
              activeFilters.current.delete(lastClickedFilterButton);
            }
          }
          setModalOpen(false);
        }

        // Check if it's a cancel/close button or clicking outside modal
        else if (
          event.target.closest('.cancel-button') ||
          event.target.closest('..tr-Button--tertiary') ||
          event.target.classList.contains('modal-backdrop')
        ) {
          setModalOpen(false);
        }
      } catch (error) {
        console.error('Error in modal button click handler:', error);
      }
    };

    // Check for modal close by escape key
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && modalOpen) {
        setModalOpen(false);
      }
    };

    // Monitor for modal state changes
    const checkModalInterval = setInterval(() => {
      const isModalCurrentlyOpen = checkIfModalOpen();

      // If modal was open but now closed (without clicking buttons)
      if (modalOpen && !isModalCurrentlyOpen) {
        setModalOpen(false);
      }
    }, 300);

    document.addEventListener('click', handleFilterButtonClick);
    document.addEventListener('click', handleModalButtonClick);
    document.addEventListener('keydown', handleKeyDown);

    if (serachFlag) {
      try {
        // Reset all filter buttons
        document
          .querySelectorAll('.tr-DataGrid-filter')
          .forEach((button, index) => {
            if (activeFilters.current.has(index)) {
              button.innerHTML = inactiveSVGString;
            }
          });
        activeFilters.current.clear();
      } catch (error) {
        console.error('Error resetting filters:', error);
      }
    }

    return () => {
      document.removeEventListener('click', handleFilterButtonClick);
      document.removeEventListener('click', handleModalButtonClick);
      document.removeEventListener('keydown', handleKeyDown);
      clearInterval(checkModalInterval);
    };
  }, [serachFlag, lastClickedFilterButton, modalOpen]);

  // This effect handles the case when filter icon is clicked again to close the modal
  useEffect(() => {
    const handleFilterIconClick = (event) => {
      try {
        const filterButton = event.target.closest('.tr-DataGrid-filter');
        if (!filterButton) return;

        const buttonId = Array.from(
          document.querySelectorAll('.tr-DataGrid-filter')
        ).indexOf(filterButton);

        // If we're clicking on the same filter button that opened the modal
        if (modalOpen && buttonId === lastClickedFilterButton) {
          // If this filter wasn't actually applied (just opened and closed)
          if (!activeFilters.current.has(buttonId)) {
            filterButton.innerHTML = inactiveSVGString;
          }
        }
      } catch (error) {
        console.error('Error in filter icon click handler:', error);
      }
    };

    document.addEventListener('click', handleFilterIconClick);

    return () => {
      document.removeEventListener('click', handleFilterIconClick);
    };
  }, [modalOpen, lastClickedFilterButton]);

  const renderPagination = (paginationProps: IPaginationBarProps) => {
    const {
      pageCount,
      pageSize: itemsPerPage,
      numberOfVisiblePages,
      currentPage,
      rowsPerPageValues,
      onPageChange,
      onPageSizeChange,
      totalItemsCount,
    } = paginationProps;

    return (
      <div>
        <DynamicPagination
          numberOfVisiblePages={numberOfVisiblePages}
          nextButtonLabel={NEXT_LABEL}
          previousButtonLabel={PREVIOUS_LABEL}
          paginationLabel={PAGINATION_LABEL}
          rowsPerPageLabel={RESULTS_PER_PAGE_LABEL}
          rowsPerPageLabelContent={ROWS_PER_PAGE_LABEL_CONTENT}
          rowsPerPageValues={rowsPerPageValues}
          viewingLabel={PAGE_LABEL}
          goToPageError={ERROR_MESSAGE}
          totalPages={pageCount}
          totalItems={totalItemsCount}
          pageSize={itemsPerPage}
          startFromPage={currentPage}
          onPageChange={(pageChangeItem) => {
            onPageChange(pageChangeItem);
          }}
          onPageSizeChange={(pageSizeItem) => {
            onPageSizeChange(pageSizeItem);
          }}
        />
      </div>
    );
  };

  return (
    <div className={getClassName()}>
      <FlexGrid>
        <FlexGrid.Row className={getClassName({ descendantName: 'flexRow' })}>
          <FlexGrid.Col
            lg={12}
            sm={12}
            className={getClassName({ descendantName: 'tableResults' })}
          >
            <DataGrid
              columns={columns}
              data={data}
              sorting={{
                ariaSortDescendingName: 'descending',
                ariaSortAscendingName: 'ascending',
                disableMultiSort: true,
                manualSortBy: false,
                autoResetSortBy: false,
                disableSortRemove: false,
              }}
              renderPagination={renderPagination}
              enableShowMore
              maxTextLength={250}
              pagination={{
                itemsPerPageValues: PAGINATION_CONFIG,
                itemsPerPage: ITEMS_PER_PAGE,
              }}
              clearFilterFlag={serachFlag}
            />
          </FlexGrid.Col>
        </FlexGrid.Row>
      </FlexGrid>
    </div>
  );
};

FileDownloadExperienceSearchResultDekstop.displayName =
  'FileDownloadExperienceSearchResultDekstop';
